import { useState } from 'react'
import PropTypes from 'prop-types'
import { shade, darken } from 'polished'
import { useTheme } from '@emotion/react'
import clsx from 'clsx'
import {
  Checkbox,
  Inline,
  List,
  ListItem,
  Select,
  Toggle,
} from 'chaoskit/src/components'
import { generateGradient } from 'chaoskit/src/assets/styles/utility/gradient'
import { StylesContainerVariables } from 'chaoskit/src/components/Container'
import { text } from 'chaoskit/src/assets/styles/utility'

import KubernetesGraph from './KubernetesGraph'

import Icon from '~components/Icon'
import { shadowEmboss } from '~styles/utility'
import background from '~media/product/kubernetes/ui-background.svg'

const StylesKubernetesUI = (theme) => ({
  deployment: '#6e40aa',
  statefulset: '#407de0',
  daemonset: '#1ac7c2',
  replicaset: '#23c386',
  pod: '#adf37b',
  block: {
    content: "''",
    display: 'block',
    marginTop: theme.space.small,
    width: 140,
    background: generateGradient({
      start: shade(0.125, theme.color.panel.base),
      stop: darken(0.05, theme.color.panel.base),
    }),
    height: 8,
    borderRadius: 8 / 2,
  },
})

const K8sLegendItem = ({ type, ...rest }) => {
  const theme = useTheme()

  return (
    <div
      css={{
        display: 'grid',
        gridTemplateColumns: 'auto 1fr',
        gap: theme.space.small,
        position: 'relative',
        alignItems: 'center',
        fontSize: theme.fontSize.small,

        '&::before': {
          content: "''",
          width: theme.fontSize.base,
          height: theme.fontSize.base,
          borderRadius: theme.borderRadius.rounded,
          display: 'block',
          background: StylesKubernetesUI(theme)[type],
        },
      }}
      {...rest}
    />
  )
}

K8sLegendItem.propTypes = {
  type: PropTypes.oneOf([
    'deployment',
    'statefulset',
    'daemonset',
    'replicaset',
    'pod',
  ]),
}

const K8sTargetCount = ({ fill, ...rest }) => {
  const theme = useTheme()

  return (
    <div
      css={[
        {
          fontSize: theme.fontSize.xsmall,
          fontWeight: theme.fontWeight.bold,
          paddingTop: theme.space.xsmall,
          paddingBottom: theme.space.xsmall,
          paddingLeft: theme.space.small,
          paddingRight: theme.space.small,
          lineHeight: 1,
          display: 'inline-block',
          userSelect: 'none',
          borderRadius: theme.fontSize.xsmall + theme.space.xsmall / 2 / 2,
        },

        fill && {
          background: shade(0.25, theme.color.panel.base),
          color: theme.contrast.base,
        },

        !fill && {
          border: theme.border.base,
          color: theme.fontColor.muted,

          '&.is-active': {
            color: theme.brand.blue,
            borderColor: theme.brand.blue,
          },
        },
      ]}
      {...rest}
    />
  )
}

K8sTargetCount.propTypes = {
  fill: PropTypes.bool,
}

const K8sTargetSection = (props) => {
  const theme = useTheme()

  return (
    <div
      css={{
        paddingTop: theme.space.medium / 2,
        paddingBottom: theme.space.medium / 2,
        paddingLeft: theme.space.medium,
        paddingRight: theme.space.medium,

        '+ *': {
          borderTop: theme.border.base,
        },
      }}
      {...props}
    />
  )
}

const K8sSection = ({ label, selection, params, children, ...rest }) => {
  const theme = useTheme()

  const activeCount = selection.filter((item) => params.includes(item)).length

  return (
    <K8sTargetSection {...rest}>
      <div
        css={{
          display: 'grid',
          gridTemplateColumns: '1fr auto',
          gap: theme.space.small,
          alignItems: 'center',
        }}
      >
        <div
          css={{
            fontWeight: theme.fontWeight.base,
          }}
        >
          {label}
        </div>
        <K8sTargetCount fill>
          {activeCount} of {params.length} selected
        </K8sTargetCount>
      </div>
      {children}
    </K8sTargetSection>
  )
}

K8sSection.propTypes = {
  label: PropTypes.string.isRequired,
  selection: PropTypes.array.isRequired,
  params: PropTypes.array.isRequired,
  children: PropTypes.node.isRequired,
}

const K8sSelect = (props) => {
  const theme = useTheme()

  return (
    <Select
      wrapperProps={{
        css: {
          label: {
            fontWeight: theme.fontWeight.base,
          },
        },
      }}
      onChange={() => {}}
      {...props}
    />
  )
}

const K8sCheckbox = ({ label, handleSelection, selection, pills }) => {
  const theme = useTheme()

  return (
    <div
      css={{
        display: 'grid',
        gap: theme.space.small,

        [theme.mq.medium]: {
          gridTemplateColumns: '1fr auto',
        },
      }}
    >
      <Checkbox
        name={label}
        onChange={handleSelection}
        label={label}
        value={label}
        checked={selection.includes(label)}
        css={{
          fontSize: theme.fontSize.small,
        }}
      />
      {pills.length > 0 && (
        <div>
          <Inline size="small">
            {pills.map((pill) => (
              <ListItem key={pill}>
                <K8sTargetCount
                  className={clsx({
                    [theme.settings.classes.active]: selection.includes(label),
                  })}
                >
                  {pill}
                </K8sTargetCount>
              </ListItem>
            ))}
          </Inline>
        </div>
      )}
    </div>
  )
}

K8sCheckbox.propTypes = {
  label: PropTypes.string.isRequired,
  pills: PropTypes.array.isRequired,
  selection: PropTypes.array.isRequired,
  handleSelection: PropTypes.func.isRequired,
}

const KubernetesUI = () => {
  const theme = useTheme()

  const [selection, setSelection] = useState([])
  const objects = [
    'Deployment-1',
    'Deployment-2',
    'StatefulSet-1',
    'DaemonSet-1',
    'DaemonSet-2',
  ]

  const handleSelection = ({ target: { name, checked } }) => {
    setSelection((currentSelections) => {
      const selectionsSet = new Set(currentSelections)

      if (!checked) {
        selectionsSet.delete(name)
        return Array.from(selectionsSet)
      }

      selectionsSet.add(name)
      return Array.from(selectionsSet)
    })
  }

  const handleTargetAll = ({ target: { checked } }) => {
    if (checked) {
      setSelection(objects)
    } else {
      setSelection([])
    }
  }

  return (
    <div
      css={{
        background: `url(${background}) center center no-repeat`,
        backgroundSize: 'contain',
      }}
    >
      <div
        css={[
          shadowEmboss(theme, {
            size: '20px',
          }),
          {
            maxWidth: StylesContainerVariables.small,
            border: theme.border.base,
            borderRadius: theme.borderRadius.base,
            margin: '0 auto',
          },
        ]}
      >
        <header
          css={{
            borderTopLeftRadius: theme.borderRadius.base,
            borderTopRightRadius: theme.borderRadius.base,
            padding: theme.space.medium,
            background: theme.color.panel.base,
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            gap: theme.space.base,
          }}
        >
          <Icon
            icon="kubernetes"
            css={{
              fontSize: theme.height.xsmall,
              color: theme.color.primary.base,
            }}
          />
          <h4
            css={{
              margin: 0,
              fontWeight: theme.fontWeight.bold,
              fontSize: theme.fontSize.base,
              position: 'relative',

              '&::after': StylesKubernetesUI(theme).block,
            }}
          >
            Choose objects to target
          </h4>
        </header>
        <div
          css={{
            background: theme.color.panel.base,
            borderTop: theme.border.base,
            padding: theme.space.medium,
            display: 'grid',
            gap: theme.space.medium,

            [theme.mq.medium]: {
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: theme.space.medium * 2,
            },
          }}
        >
          <div>
            <K8sSelect label="1. Choose a cluster" name="cluster" required>
              <option value="myCluster">myCluster</option>
            </K8sSelect>
          </div>
          <div>
            <K8sSelect label="2. Choose a namespace" name="namespace">
              <option value="staging">Staging</option>
            </K8sSelect>
          </div>
        </div>
        <div
          css={{
            background: theme.color.light.base,
            borderTop: theme.border.base,
            display: 'grid',
            borderBottomLeftRadius: theme.borderRadius.base,
            borderBottomRightRadius: theme.borderRadius.base,

            [theme.mq.medium]: {
              gridTemplateColumns: 'repeat(2, 1fr)',
            },
          }}
        >
          <div>
            <K8sTargetSection>
              <Toggle
                name="targetAll"
                label="Target all objects"
                onChange={handleTargetAll}
                value="toggle"
                checked={selection.length === objects.length}
              />
            </K8sTargetSection>
            <K8sSection
              label="Deployments"
              selection={selection}
              params={['Deployment-1', 'Deployment-2']}
            >
              <List space="small">
                <ListItem>
                  <K8sCheckbox
                    label="Deployment-1"
                    pills={['1 ReplicaSet', '1 Pod']}
                    handleSelection={handleSelection}
                    selection={selection}
                  />
                </ListItem>
                <ListItem>
                  <K8sCheckbox
                    label="Deployment-2"
                    pills={['7 Pods']}
                    handleSelection={handleSelection}
                    selection={selection}
                  />
                </ListItem>
              </List>
            </K8sSection>
            <K8sSection
              label="StatefulSets"
              selection={selection}
              params={['StatefulSet-1']}
            >
              <List space="small">
                <ListItem>
                  <K8sCheckbox
                    label="StatefulSet-1"
                    pills={['2 Pods']}
                    handleSelection={handleSelection}
                    selection={selection}
                  />
                </ListItem>
              </List>
            </K8sSection>
            <K8sSection
              label="DaemonSets"
              selection={selection}
              params={['DaemonSet-1', 'DaemonSet-2']}
            >
              <List space="small">
                <ListItem>
                  <K8sCheckbox
                    label="DaemonSet-1"
                    pills={['1 Pod']}
                    handleSelection={handleSelection}
                    selection={selection}
                  />
                </ListItem>
                <ListItem>
                  <K8sCheckbox
                    label="DaemonSet-2"
                    pills={['1 Pod']}
                    handleSelection={handleSelection}
                    selection={selection}
                  />
                </ListItem>
              </List>
            </K8sSection>
          </div>
          <div
            css={{
              background: theme.color.panel.base,
              padding: theme.space.medium,
              borderBottomRightRadius: theme.borderRadius.base,

              [theme.mq.small__max]: {
                borderBottomLeftRadius: theme.borderRadius.base,
                borderTop: theme.border.base,
              },

              [theme.mq.medium]: {
                borderLeft: theme.border.base,
              },
            }}
          >
            <div
              css={{
                textAlign: 'center',

                '&::after': {
                  ...StylesKubernetesUI(theme).block,
                  marginLeft: 'auto',
                  marginRight: 'auto',
                },
              }}
            >
              <div
                css={{
                  ...text.expanded(theme),
                  ...theme.text.xsmall,
                  fontWeight: theme.fontWeight.bold,
                }}
              >
                Blast Radius
              </div>
              <div
                css={{
                  fontWeight: theme.fontWeight.bold,
                  fontSize: theme.fontSize.large,
                  marginTop: theme.space.xsmall,
                }}
              >
                {selection.length} of 5
              </div>
            </div>

            <KubernetesGraph
              css={{
                maxWidth: 300,
                marginTop: theme.space.large,
                marginBottom: theme.space.large,
                position: 'relative',
                left: '50%',
                transform: 'translateX(-50%)',

                '*': {
                  transition: `fill ${theme.timing.base} ${theme.transition.base}`,
                },
              }}
              selection={selection}
            />

            <div
              css={{
                display: 'grid',
                gap: theme.space.small,

                [theme.mq.medium]: {
                  gridTemplateColumns: 'repeat(2, auto)',
                  width: '75%',
                  marginLeft: 'auto',
                  marginRight: 'auto',
                },
              }}
            >
              <K8sLegendItem type="deployment">Deployment</K8sLegendItem>
              <K8sLegendItem type="statefulset">StatefulSet</K8sLegendItem>
              <K8sLegendItem type="daemonset">DaemonSet</K8sLegendItem>
              <K8sLegendItem type="replicaset">ReplicaSet</K8sLegendItem>
              <K8sLegendItem type="pod">Pod</K8sLegendItem>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default KubernetesUI
